import React, { useEffect, useMemo, useState } from "react";
import { Form, Formik } from "formik";
import { navigate } from "gatsby";

import { getTranslatedCityName } from "../utils/getTranslatedCityName";
import { constructUrl } from "../utils/constructUrl";
import { usePageSettings } from "../contexts/PageSettingsContext";
import { useSelectedPlanContext } from "../contexts/SelectedPlanContext";

import enTranslations from "../locales/en/translations.json";
import esTranslations from "../locales/es/translations.json";

import SeoHeader from "../components/ui/layout/SeoHeader";
import CheckoutFormTabs from "../components/ui/checkout/CheckoutFormTabs";

import { PrimaryOutlineButton } from "../components/ui/inputs/buttons/Button";
import { CheckoutProvider } from "../contexts/CheckoutPageContext";
import { getInitialValues, getValidationSchema } from "../utils/checkoutValidationSchemas";
import CheckoutLayout from "../components/ui/checkout/CheckoutLayout";
import { Link } from "gatsby";

// If you had a buildOrderPayload or final handleSubmit logic here before,
// it will be removed since we do all that in OrderSummary now.

const CheckoutPageTemplate = ({ pageContext }) => {
	const { loadPageTranslations, loadPageSettingsContext, changeLanguage, currency, selectedLocation } =
		usePageSettings();
	const { selectedPlan } = useSelectedPlanContext();

	const { city, language, country, hasCityInUrl, isDf, isHomepage } = pageContext;
	const citySlug = hasCityInUrl ? getTranslatedCityName(language, city) : null;
	const countrySlug = isDf ? "df" : isHomepage ? null : country;
	const legalUrl = constructUrl(language, countrySlug, citySlug, "/legal");

	// Decide which translation file to use
	let { translations } = usePageSettings();
	switch (language) {
		case "en":
			translations = enTranslations;
			break;
		case "es":
			translations = esTranslations;
			break;
		default:
			translations = enTranslations;
	}

	// Keep track of current tab in parent state
	const initialTab = typeof window !== "undefined" ? parseInt(sessionStorage.getItem("checkout-tab") || "0", 10) : 0;
	const [currentTab, setCurrentTab] = useState(initialTab);

	// On mount, load language & page settings
	useEffect(() => {
		loadPageSettingsContext(pageContext);
		loadPageTranslations(translations);
		changeLanguage(language);
	}, [language]);

	useEffect(() => {
		sessionStorage.setItem("checkout-tab", currentTab);
	}, [currentTab]);

	// Pick initial values & schema based on selectedPlan
	const initialValues = useMemo(
		() => getInitialValues(selectedPlan.service, translations),
		[selectedPlan.service, translations]
	);
	const validationSchema = useMemo(
		() => getValidationSchema(selectedPlan.service, translations),
		[selectedPlan.service, translations]
	);

	// Addons state (only if you need to track additional items)
	const [addons, setAddons] = useState([]);

	const addAddon = (addonType, addonId, addonName) => {
		setAddons((prev) => {
			const existing = prev.find((addon) => addon.type === addonType);
			if (existing) {
				// Replace existing
				return prev.map((addon) =>
					addon.type === addonType ? { type: addonType, id: addonId, name: addonName } : addon
				);
			}
			// Add new
			return [...prev, { type: addonType, id: addonId, name: addonName }];
		});
	};

	const removeAddon = (addonType) => {
		setAddons((prev) => prev.filter((addon) => addon.type !== addonType));
	};

	// If user tries to visit this page without selecting a plan
	if (!selectedPlan.service) {
		return (
			<div className="bg-gradient-to-l from-[#09090B] to-[#18181B] h-screen w-screen flex flex-col items-center justify-center gap-12 max-md:px-7">
				<h1 className="text-neutral-50 text-4xl font-normal">{translations["CheckoutEmptyServiceTitle"]}</h1>
				<div className="flex flex-col gap-4 justify-center items-center text-sm text-neutral-50">
					<p className="text-center text-base font-normal">{translations["CheckoutEmptyServiceIntro"]}</p>
					<p className="text-center">
						{translations["CheckoutEmptyServiceQuestion"]}{" "}
						<Link
							to={constructUrl(
								pageContext.language,
								pageContext.country,
								pageContext.city,
								"/contact-us"
							)}
						>
							<b className="font-bold hover:underline hover:underline-offset-8">
								{translations["CheckoutEmptyServiceQuestionLink"]}
							</b>
						</Link>
					</p>
				</div>
				<PrimaryOutlineButton onClick={() => navigate("/")}>
					{translations["CheckoutEmptyServiceButtonText"]}
				</PrimaryOutlineButton>
			</div>
		);
	}

	// The form
	return (
		<CheckoutLayout pageContext={pageContext}>
			<CheckoutProvider>
				<Formik
					// We do partial validation in tabs & final submission in OrderSummary
					validateOnMount={true}
					validateOnChange={true}
					validateOnBlur={true}
					initialValues={initialValues}
					validationSchema={validationSchema}
					onSubmit={() => {
						// We intentionally leave this empty or minimal,
						// because the final order creation logic is in OrderSummary.
						// console.log("Formik onSubmit called (no-op).");
					}}
				>
					<Form>
						<div className="container flex mx-auto my-[74px] md:my-[156px] w-full flex-col gap-16 h-full flex-grow">
							<div id="scroll-target" className="flex w-full justify-between items-center px-4 md:px-0">
								{/* MAIN TITLE */}
								<div className="flex flex-col gap-2.5">
									<h1 className="text-4xl font-normal">
										{translations["CheckoutEmptyServiceTitle"]}
									</h1>
									<h2 className="text-lg font-normal text-ipxon-magenta">
										{translations["CheckoutEmptyServiceSubtitle"]}
									</h2>
								</div>
								<p className="hidden md:flex gap-2 text-xs text-ipxon-white">
									{translations["CheckoutEmptyServiceQuestion"]}{" "}
									<Link
										to={constructUrl(
											pageContext.language,
											pageContext.country,
											pageContext.city,
											"/contact-us"
										)}
									>
										<b className="font-bold hover:underline hover:underline-offset-8">
											{translations["CheckoutEmptyServiceQuestionLink"]}
										</b>
									</Link>
								</p>
							</div>

							{/* TABS & SUMMARY */}
							{/* Tabs */}
							<CheckoutFormTabs
								currentTab={currentTab}
								setCurrentTab={setCurrentTab}
								addons={addons}
								addAddon={addAddon}
								removeAddon={removeAddon}
								legalUrl={legalUrl}
							/>
						</div>
					</Form>
				</Formik>
			</CheckoutProvider>
		</CheckoutLayout>
	);
};

export const Head = ({ pageContext }) => <SeoHeader pageContext={pageContext} />;

export default CheckoutPageTemplate;

export async function getServerData() {
	return {
		props: {},
	};
}
